import React, { useState, useEffect } from "react";
import {
    Box,
    Button,
    Card,
    CardContent,
    Grid,
    Paper,
    Typography,
    TextField,
    Dialog
} from "@mui/material";
import { useAuth, useNavigation, useLoading, useSnackbar } from "../../../Context/Contexts";
import PrintIcon from "@mui/icons-material/Print";
import { ListQuery } from "../../../Types/CommonTypes";
import { PrinterViewModel, PrintNodeViewModel } from "../../../Types/ExportTypes";
import CenteredListCard from "../../../components/List/CenteredListCardLayout";
import ListCardHeader from "../../../components/List/ListCardHeader";
import useCachedListQuery from "../../../hooks/useCachedListQuery";
import { useTranslation } from "react-i18next";
import LoadingSection from "../../../components/LoadingSection";
import ResponsivePagination from "../../../components/ResponsivePagination";

const PrinterList: React.FC = () => {
    const [items, setItems] = useState<PrinterViewModel[]>([]);
    const { t } = useTranslation();
    const [printNodeViewModel, setPrintNodeViewModel] = useState<PrintNodeViewModel | null>(null);
    const [totalCount, setTotalCount] = useState(0);
    const [query, setQuery] = useCachedListQuery({
        page: 0,
        pageSize: 10,
        filter: "",
    });
    const [filter, setFilter] = useState<string>("");
    const [loading, setLoading] = useState(false);

    const { authService } = useAuth();
    const { navigate } = useNavigation();
    const { showLoading, hideLoading } = useLoading();
    const { showSnackbar } = useSnackbar();
    const [showPrintNodeModal, setShowPrintNodeModal] = useState(false);
    const fetchItems = async () => {
        setLoading(true);
        try {
            const response = await authService.post<{ items: PrinterViewModel[]; count: number }>(
                "/settingsApi/GetPrinterList",
                query
            );
            setItems(response.items);
            setTotalCount(response.count);
        } catch (error : any) {
            showSnackbar(`${t("common.failedToFetchEntries")}: ${error.response?.data ?? error.message}`, "error");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchItems();
    }, [query]);

    useEffect(() => {
        if (loading) showLoading();
        else hideLoading();
    }, [loading, showLoading, hideLoading]);

    const handleSearchSubmit = (event: React.FormEvent) => {
        event.preventDefault();
        setQuery({ ...query, filter: filter, page: 0 });
    };

    const handlePageChange = (newPage: number) => {
        setQuery({ ...query, page: newPage });
    };

    const handleSelect = (id?: number | null) => {
        navigate({ path: "/settings/printer/edit", params: { id } });
    };

    const handleCreate = () => {
        navigate({ path: "/settings/printer/edit", params: {  } });
    };


    const togglePrintNode = async () => {

        if (!printNodeViewModel) {
            setLoading(true);
            try {
                const response = await authService.get<PrintNodeViewModel>(
                    "/settingsApi/GetPrintNode"
                );
                setPrintNodeViewModel(response);
            } catch (error: any) {
                showSnackbar(`${t("common.failedToFetchEntries")}: ${error.response?.data ?? error.message}`, "error");
            } finally {
                setLoading(false);
            }
        }
        setShowPrintNodeModal(!showPrintNodeModal);
    };


    const activatePrintNode = async () => {


            setLoading(true);
            try {
                const response = await authService.post<PrintNodeViewModel>(
                    "/settingsApi/ActivatePrintNode", {}
                );
                setPrintNodeViewModel(response);
            } catch (error: any) {
                showSnackbar(`${t("dialog.activatePrintNodeFailed")} : ${error.response?.data ?? error.message}`, "error")

            } finally {
                setLoading(false);
            }
        
    };
    return (
        <CenteredListCard title={ t("common.printers")}> 
   
            <Box sx={{ marginBottom: 2 }}>
                <Button
                    variant="contained"
                    color="success"
                    size="small"
                    onClick={handleCreate}
                >
                        {t("common.create")}
                    </Button>
                    <Button
                        variant="contained"
                        color="info"
                        size="small"
                        onClick={togglePrintNode}
                    >
                        PrintNode info
                    </Button>
            </Box>
            <Box
                component="form"
                onSubmit={handleSearchSubmit}
                sx={{
                    display: "flex",
                    gap: 2,
                    marginBottom: 2,
                }}
            >
                <TextField
                        label={t("common.search")}
                    variant="outlined"
                    fullWidth
                    onChange={(event) => setFilter(event.target.value)}
                    value={filter}
                    disabled={loading}
                />
                <Button
                    variant="contained"
                    color="primary"
                    type="submit"
                    disabled={loading}
                >
                        {t("common.search")}
                </Button>
            </Box>


        <LoadingSection loading={loading}>
            <Grid container spacing={2} sx={{ mb: 2 }}>
                {items.map((item) => (
                    <Grid item
                        xs={12}
                        sm={items.length === 1 ? 12 : items.length === 2 ? 6 : 6}
                        md={items.length === 1 ? 12 : items.length === 2 ? 6 : 4}
                        key={item.id}>
                        <Card
                            onClick={() => handleSelect(item.id)}
                            sx={{
                                cursor: "pointer",
                                transition: "box-shadow 0.2s ease-in-out",
                                "&:hover": {
                                    boxShadow: 4,
                                    backgroundColor: "secondary.main",
                                },
                            }}
                        >
                            <CardContent>
                                <ListCardHeader
                                    icon={<PrintIcon fontSize="small" color="action" />}
                                    title={item.printerIdentity}
                                />
                                <Typography variant="body2" color="textSecondary">
                                    Id: {item.id}
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                    Printnode Id: {item.printNodeId}
                                </Typography>
                            
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
                {items.length === 0 && (
                    <Grid item xs={12}>
                            <Typography align="center">{t("common.noEntriesFound")}</Typography>
                    </Grid>
                )}
            </Grid>
        </LoadingSection>

            {/* Pagination */}
            <ResponsivePagination

                page={query.page}

                pageSize={query.pageSize}

                totalCount={totalCount}

                onPageChange={handlePageChange}

            />
         
            <Dialog
                open={showPrintNodeModal}
                onClose={togglePrintNode}
                fullWidth
                maxWidth="md"
                PaperProps={{ sx: { borderRadius: "8px" } }}
            >
                <Box sx={{ p: 4 }}>
                    <Typography variant="h6" sx={{ marginBottom: 2 }}>
                        Print Node
                    </Typography>

                    <TextField
                        label={t("common.apiKey")}
                        value={printNodeViewModel?.apiKey}
                        fullWidth
                        margin="normal"
                        disabled
                    />

                    <TextField
                        label={t("common.username")}
                        value={printNodeViewModel?.userName}
                        fullWidth
                        margin="normal"
                        disabled
                    />

                    <TextField
                        label={t("common.password")}
                        value={printNodeViewModel?.password}
                        fullWidth
                        margin="normal"
                        disabled
                    />

                    {printNodeViewModel && !printNodeViewModel.activated && (
                        <>
                    <Button fullWidth variant="contained" onClick={activatePrintNode}>
                                {t("common.activatePrintNode")}
                    </Button>
                    </>
                    )}
                    <Box sx={{ display: "flex", justifyContent: "flex-end", marginTop: 2 }}>
                        <Button variant="contained" onClick={togglePrintNode}>
                            {t("common.close")}
                        </Button>
                    </Box>
                </Box>
            </Dialog>
        </CenteredListCard>

    );
};

export default PrinterList;
